<script setup>
import { onMounted, ref, useTemplateRef, watch } from 'vue'
import { useAuthStore } from './stores/auth'
import { useTOOStore } from './stores/too'
import ClientView from './views/ClientView.vue'
import StructureView from './views/StructureView.vue'
import AppBackground from './components/AppBackground.vue'
import SummaryView from './views/SummaryView.vue'
import { useStoryblokStore } from './stores/storyblok'
import TemplateView from './views/TemplateView.vue'

const items = [
  {
    title: 'Client',
    icon: 'i-lucide-briefcase',
    slot: 'client',
    ref: useTemplateRef('client'),
  },
  {
    title: 'Structure',
    icon: 'i-lucide-list-tree',
    slot: 'structure',
    ref: useTemplateRef('structure'),
  },
  {
    title: 'Templates',
    icon: 'i-lucide-clipboard-pen-line',
    slot: 'template',
    ref: useTemplateRef('template'),
  },
  {
    title: 'Summary',
    icon: 'i-lucide-wand',
    slot: 'summary',
    ref: useTemplateRef('summary'),
  },
]

const isProd = import.meta.env.PROD

const authStore = useAuthStore()
const storyblokStore = useStoryblokStore()
const tooStore = useTOOStore()

const currentStep = ref(0)
const stepper = useTemplateRef('stepper')

async function goNext() {
  const currentView = items[currentStep.value]?.ref
  if (currentView) {
    const valid = await currentView.value.validate()
    if (!valid) return
    currentStep.value++
  }
}

function goPrevious() {
  currentStep.value--
}

function generate() {
  storyblokStore.generateSite()
}

onMounted(() => {
  window.addEventListener('beforeunload', function (e) {
    if (storyblokStore.progress > 0 && storyblokStore.progress < 100) {
      e.preventDefault()
      e.returnValue = ''
    }
  })
})

const missingSiteSlug = ref(false)
watch(
  () => authStore.isAdmin,
  async (isAdmin) => {
    if (isAdmin) {
      const pathSegments = window.location.pathname.split('/')
      const siteSlug = pathSegments[1]
      if (siteSlug) {
        const site = await tooStore.fetchSite(siteSlug)
        if (site) {
          storyblokStore.siteSlug = siteSlug
        } else {
          missingSiteSlug.value = true
        }
      } else {
        missingSiteSlug.value = true
      }
    }
  },
)

watch(
  () => storyblokStore.progress,
  (newProgress) => {
    if (newProgress === 0) {
      currentStep.value = 0
    }
  },
)
</script>

<template>
  <UApp>
    <AppBackground />

    <UHeader
      :to="null"
      class="bg-transparent"
      :ui="{
        container: 'lg:px-0',
        title: 'text-white hover:text-white text-shadow-md text-shadow-gray-900 items-center gap-4',
      }"
    >
      <template #title>
        <UIcon name="i-hugeicons-block-game" class="size-10 text-primary"></UIcon>
        <h1>{{ storyblokStore.siteSlug }}</h1>
      </template>
      <template #right>
        <UUser
          :name="authStore.user?.email"
          :ui="{
            name: 'text-white text-xs',
          }"
        >
          <template #avatar>
            <UIcon name="i-lucide-circle-user-round" class="size-5 text-white"></UIcon>
          </template>
        </UUser>
        <UButton v-if="!isProd" @click="authStore.logout()" class="ml-2">Logout</UButton>
      </template>
    </UHeader>

    <UMain as="main" class="flex items-center">
      <UContainer class="bg-white h-fit rounded-lg p-8 z-1 my-12">
        <div
          v-if="authStore.isLoading"
          class="h-64 flex flex-col items-center justify-center gap-4"
        >
          <UIcon name="i-lucide-loader-circle" class="size-20 text-primary animate-spin"></UIcon>
        </div>
        <div
          v-else-if="!authStore.isAdmin"
          class="h-64 flex flex-col items-center justify-center gap-4"
        >
          <UIcon name="i-lucide-triangle-alert" class="size-30 text-warning"></UIcon>
          <p class="text-lg">You must be an administrator to use this tool</p>
        </div>
        <div
          v-else-if="missingSiteSlug"
          class="h-64 flex flex-col items-center justify-center gap-4"
        >
          <UIcon name="i-lucide-globe" class="size-30 text-warning"></UIcon>
          <p class="text-lg">Site slug is missing from URL or couldn't be found in the Publisher</p>
        </div>
        <template v-else>
          <UStepper
            ref="stepper"
            v-model="currentStep"
            disabled
            :items="items"
            :ui="{ content: storyblokStore.progress ? 'pt-8 pb-6 px-24' : 'pt-8 pb-12 px-24' }"
          >
            <template #client>
              <ClientView ref="client" @completed="stepper.next()"></ClientView>
            </template>
            <template #structure>
              <StructureView ref="structure"></StructureView>
            </template>
            <template #template>
              <TemplateView ref="template"></TemplateView>
            </template>
            <template #summary>
              <SummaryView ref="summary"></SummaryView>
            </template>
          </UStepper>

          <div class="flex gap-2 justify-between" v-if="!storyblokStore.progress">
            <UButton
              leading-icon="i-lucide-arrow-left"
              v-if="stepper?.hasPrev"
              @click="goPrevious()"
            >
              Previous
            </UButton>

            <UButton
              class="ml-auto"
              trailing-icon="i-lucide-arrow-right"
              v-if="stepper?.hasNext"
              @click="goNext()"
            >
              Next
            </UButton>

            <UButton
              class="ml-auto"
              trailing-icon="i-lucide-wand"
              v-if="!stepper?.hasNext"
              @click="generate()"
            >
              Generate
            </UButton>
          </div>
        </template>
      </UContainer>
    </UMain>

    <!-- <UFooter /> -->
  </UApp>
</template>
